import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Form,
  Input,
  Label,
  FormFeedback,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  FormGroup,
  Spinner,
  OffcanvasBody,
  Offcanvas,
  OffcanvasHeader,
} from "reactstrap";
import WaiterService from "../../../services/OrderingServices/WaiterService";
import toast from "react-hot-toast";
import Select from "react-select";
import OrganizationService from "../../../services/OrderingServices/OrganizationService";
import SimpleBar from "simplebar-react";

const WaiterForm = ({ isOpen, toggle, selectedItem, fetchData }) => {
  const [passwordShow, setPasswordShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roleData, setRoleData] = useState([]);

  const [detail, setDetail] = useState();
  const fetchDetail = async () => {
    try {
      const response = await OrganizationService.orgDetail(selectedItem?.id);
      setDetail(response);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchDetail();
  }, [selectedItem?.id]);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: detail?.firstName || "",
      lastName: detail?.lastName || "",
      email: detail?.email || "",
      phoneNumber: detail?.phoneNumber ? String(detail.phoneNumber) : "",
      address: detail?.address || "",
      password: detail?.password || "",
      roles: detail?.roles || [], // Initialize as empty array
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      phoneNumber: Yup.string()
        .required("Phone number is required")
        .matches(/^[0-9]+$/, "Phone number must be digits only")
        .min(10, "Phone number must be at least 10 digits")
        .max(10, "Phone number must be exactly 10 digits"),
      address: Yup.string().required("Address is required"),
      email: Yup.string()
        .email("Invalid email address")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Enter a valid email address"
        )
        .required("Email is required"),
      password: selectedItem?.id
        ? Yup.string().notRequired()
        : Yup.string()
            .min(6, "Password must be at least 6 characters")
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
              "Password must contain at least one uppercase, one lowercase, and one number"
            )
            .required("Password is required"),
      roles: selectedItem?.id
        ? Yup.array().notRequired()
        : Yup.array()
            .of(
              Yup.object().shape({
                id: Yup.string().required("Role ID is required"),
                name: Yup.string().required("Role name is required"),
              })
            )
            .min(1, "At least one role is required")
            .required("Roles are required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        const payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: String(values.phoneNumber),
          address: values.address,
        };

        if (!selectedItem?.id) {
          payload.email = values.email;
          payload.password = values.password;
          payload.roles = values.roles
            ? values.roles.map((role) => ({
                id: role.id,
                name: role.name,
                normalizedName: role.normalizedName || "",
                concurrencyStamp: role.concurrencyStamp || "",
              }))
            : [];
        } else {
          payload.roles = values.roles
            ? values.roles.map((role) => ({
                id: role.id,
                name: role.name,
                normalizedName: role.normalizedName || "",
                concurrencyStamp: role.concurrencyStamp || "",
              }))
            : [];
        }

        if (selectedItem?.id) {
          await WaiterService.update(selectedItem?.id, payload);
          toast.success("Waiter updated successfully");
        } else {
          await WaiterService.create(payload);
          toast.success("Waiter created successfully");
          resetForm();
        }

        fetchData();
        toggle();
      } catch (error) {
        toast.error(
          error.response?.data?.title ||
            "Failed to submit form. Please try again."
        );
        console.error("Error submitting form:", error.response?.data || error);
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchRole = async () => {
    try {
      const response = await WaiterService.authRole();
      setRoleData(response);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    fetchRole();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      formik.resetForm({
        values: {
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          address: "",
          password: "",
          roles: [], // Reset to empty array
        },
        touched: {},
        errors: {},
      });
      setPasswordShow(false);
    } else {
      formik.resetForm({
        values: {
          firstName: selectedItem?.firstName || "",
          lastName: selectedItem?.lastName || "",
          email: selectedItem?.email || "",
          phoneNumber: selectedItem?.phoneNumber
            ? String(selectedItem.phoneNumber)
            : "",
          address: selectedItem?.address || "",
          password: selectedItem?.password || "",
          roles: selectedItem?.roles || [], // Initialize as empty array
        },
        touched: {},
        errors: {},
      });
    }
  }, [isOpen, selectedItem]);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "48px",
      fontSize: "1rem",
      borderColor: state.isFocused ? "#86b7fe" : provided.borderColor,
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13,110,253,.25)" : null,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "6px 12px",
    }),
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      backdropClassName="bg-dark bg-opacity-50"
      className="shadow-lg animate__animated animate__slideInRight bg-white "
      // id="offcanvasRight"
    >
      <OffcanvasHeader toggle={toggle}>
        <div className="d-flex align-items-center text-center justify-content-center w-100 gap-2">
          <i className="ri-stack-line"></i>
          <h5 className="mb-0">Staff Form</h5>
        </div>
      </OffcanvasHeader>
      <OffcanvasBody>
        <Form onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col md={12} xs={12} className="mb-3">
              <div className="form-floating">
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  className="form-control"
                  placeholder="Enter first name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName}
                  invalid={
                    !!(formik.touched.firstName && formik.errors.firstName)
                  }
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="firstName">First Name</Label>
                <FormFeedback>{formik.errors.firstName}</FormFeedback>
              </div>
            </Col>
            <Col md={12} xs={12} className="mb-3">
              <div className="form-floating">
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  className="form-control"
                  placeholder="Enter last name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.lastName}
                  invalid={
                    !!(formik.touched.lastName && formik.errors.lastName)
                  }
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="lastName">Last Name</Label>
                <FormFeedback>{formik.errors.lastName}</FormFeedback>
              </div>
            </Col>
            <Col md={12} xs={12} className="mb-3">
              <div className="form-floating">
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="text" // Changed to text to enforce string input
                  className="form-control"
                  placeholder="Enter phone number"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.phoneNumber}
                  invalid={
                    !!(formik.touched.phoneNumber && formik.errors.phoneNumber)
                  }
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="phoneNumber">Phone Number</Label>
                <FormFeedback>{formik.errors.phoneNumber}</FormFeedback>
              </div>
            </Col>
            <Col md={12} xs={12} className="mb-3">
              <div className="form-floating">
                <Input
                  id="address"
                  name="address"
                  type="text"
                  className="form-control"
                  placeholder="Enter address"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.address}
                  invalid={!!(formik.touched.address && formik.errors.address)}
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="address">Address</Label>
                <FormFeedback>{formik.errors.address}</FormFeedback>
              </div>
            </Col>
            {!selectedItem?.id && (
              <>
                <Col md={12} xs={12} className="mb-3">
                  <div className="form-floating">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      className="form-control"
                      placeholder="Enter email"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.email}
                      invalid={!!(formik.touched.email && formik.errors.email)}
                      style={{ padding: "0.5rem", height: "1rem" }}
                    />
                    <Label for="email">Email</Label>
                    <FormFeedback>{formik.errors.email}</FormFeedback>
                  </div>
                </Col>
                <Col lg={12} md={12} xs={12} className="mb-3">
                  <div className="form-floating">
                    <Input
                      id="password"
                      name="password"
                      type={passwordShow ? "text" : "password"}
                      className="form-control pe-5"
                      placeholder="Enter Password"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                      invalid={
                        !!(formik.touched.password && formik.errors.password)
                      }
                      style={{ padding: "0.5rem", height: "1rem" }}
                    />
                    <Label for="password">Password</Label>
                    <button
                      className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted mt-2"
                      type="button"
                      onClick={() => setPasswordShow(!passwordShow)}
                      style={{
                        marginRight:
                          formik.touched.password && formik.errors.password
                            ? "18px"
                            : "0px",
                      }}
                    >
                      {passwordShow ? (
                        <i
                          className="bx bxs-show"
                          style={{ fontSize: "22px" }}
                        ></i>
                      ) : (
                        <i
                          className="bx bxs-hide"
                          style={{ fontSize: "22px" }}
                        ></i>
                      )}
                    </button>
                    <FormFeedback>{formik.errors.password}</FormFeedback>
                  </div>
                </Col>
                <Col md={12} xs={12} className="mb-3">
                  <Select
                    id="roles"
                    name="roles"
                    options={roleData}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    value={formik.values.roles}
                    onChange={(selectedOptions) => {
                      formik.setFieldValue("roles", selectedOptions || []);
                    }}
                    onBlur={() => formik.setFieldTouched("roles", true)}
                    isMulti
                    isClearable
                    placeholder="Select roles"
                    styles={{
                      ...customStyles,
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }), // ensures visibility
                    }}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    className={
                      formik.touched.roles && formik.errors.roles
                        ? "is-invalid z-1"
                        : ""
                    }
                  />
                  {formik.touched.roles && formik.errors.roles && (
                    <div className="invalid-feedback d-block">
                      {formik.errors.roles}
                    </div>
                  )}
                </Col>
              </>
            )}
          </Row>
          <div className=" d-flex bg-white  align-content-center align-items-center justify-content-between gap-2 sticky-bottom bottom-0 bg-white">
            <button
              className="btn btn-danger w-100"
              onClick={toggle}
              type="button"
            >
              Cancel
            </button>
            <Button
              color="success"
              type="submit"
              disabled={loading}
              className="w-100"
            >
              {loading && <Spinner size="sm" className="me-2" />}
              {loading
                ? selectedItem?.id
                  ? "Updating..."
                  : "Creating..."
                : selectedItem?.id
                ? "Update"
                : "Create"}
            </Button>
          </div>
        </Form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default WaiterForm;
